/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { poslumaBlue, universalStyles, poslumaDarkBlue, white } from '../../../../app/theme';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import CaseStudy from '../../../micro/caseStudy/CaseStudy';
import FindingsPanel from '../../../micro/FindingsPanel/FindingsPanel';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  heading: {
    paddingTop: 30,
  },
  whiteText: {
    color: white,
  },
  header: {
    width: '60%',
  },
  columnWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '60%',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  imageText: {
    fontSize: 12,
    textAlign: 'center',
    paddingTop: 15,
  },
  columnImage: {
    paddngRight: 50,
    width: '100%',
    maxHeight: 500,
  },
  row: {
    marginLeft: 40,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: 30,
  },
  gangliaImages: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: -30,
  },
  image1: {
    width: '60%',
    height: 450,
    paddingTop: 10,
  },
  image2: {
    width: '45%',
  },
  image: {
    width: '80%',
    height: 'auto',
    marginLeft: 90,
    marginRight: 90,
    padding: '20px 0 20px 0',
  },
  reviewFindingsText: {
    color: poslumaDarkBlue,
    paddingTop: 80,
  },
  getRidOfWhenWeGetImages: {
    paddingTop: 10,
    paddingBottom: 40,
    color: poslumaBlue,
  },
  superscript: {
    fontSize: 11,
  },
}));

type InterpretationPage13Props = {
  setNextDisabled: (disabled: boolean) => void;
  setNextDisabledCS: (disabled: boolean) => void;
};

const InterpretationPage13 = ({ setNextDisabled, setNextDisabledCS }: InterpretationPage13Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      {/* Need image here */}
      <div className={classes.poslumaBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[2].INTERPRETATION_STEP_2.path}`}
          alt={`${IMAGE_PATHS.STEPS[2].INTERPRETATION_STEP_2.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 2:</Typography>
          <Typography variant="h3" className={classes.header}>
            Evaluate POSLUMA Uptake - Interpretation Methodology
          </Typography>

          <div className={classes.columnWrapper}>
            <div className={classes.leftColumn}>
              <div className={classes.row}>
                <Typography variant="h4" className={classes.heading}>
                  Abdomen
                </Typography>
                <Typography variant="body1">
                  <strong>Assessment Procedures</strong>
                  <sup className={classes.superscript}>1</sup>:
                </Typography>
                <ul className={classes.poslumaList}>
                  <li>
                    The suggested SUV scale is 0-10 or a higher upper limit if required to ensure liver is saturated on
                    the SUV upper limit
                  </li>
                  <li>Any lymph nodes with increased uptake may represent nodal metastases</li>
                  <li>Focally increased visceral uptake is consistent with metastases</li>
                  <li>Systematically examine the para-iliac and para-aortic lymph nodes</li>
                  <li>
                    Assess the liver at a suggested SUV scale of 0-20 to identify focal abnormalities consistent with
                    metastases
                  </li>
                </ul>
              </div>
              <div className={classes.row}>
                <Typography variant="body1">
                  <strong>Possible variant uptake or interpretation challenge</strong>
                  <sup className={classes.superscript}>1</sup>:
                </Typography>
                <ul className={classes.poslumaList}>
                  <li>
                    Uptake in celiac ganglia (situated slightly below the celiac trunk) is physiologic. Celiac ganglia
                    should not be mistaken for lymph nodes
                  </li>
                  <li>Hemangiomas may exhibit increased POSLUMA uptake</li>
                </ul>
              </div>
            </div>
            <div className={classes.rightColumn}>
              <img
                className={classes.columnImage}
                src={`${BASE_URL}${IMAGE_PATHS.CELIAC_GANGLIA_ANATOMY.path}`}
                alt={`${IMAGE_PATHS.CELIAC_GANGLIA_ANATOMY.alt}`}
              />
              <Typography className={classes.imageText}>
                <strong>Anatomy of the celiac and sacral ganglia</strong>
                <sup className={classes.superscript}>1</sup>
              </Typography>
            </div>
          </div>
          <Typography variant="h4">Thorax</Typography>
          <Typography variant="body1">
            <strong>Assessment Procedures</strong>
            <sup className={classes.superscript}>1</sup>:
          </Typography>
          <ul className={classes.poslumaList}>
            <li>The suggested SUV scale is 0-10</li>
            <li>
              Any mediastinal, hilar, or axillary lymph nodes with slightly increased uptake are unlikely to represent
              nodal metastases. Exception are:
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>Focal uptake</li>
              <li>and/or Virchow node</li>
            </ul>
            <li>Scan for focal abnormalities in the lung parenchyma, which might represent lung metastases</li>
            <ul className={classes.poslumaNestedList}>
              <li>Match the image with correlative anatomical imaging</li>
            </ul>
          </ul>
          <Typography variant="body1">
            <strong>Possible variant uptake or interpretation challenge</strong>
            <sup className={classes.superscript}>1</sup>:
          </Typography>
          <ul className={classes.poslumaList}>
            <li>Cervical ganglia may show intense physiological uptake of POSLUMA</li>
            <li>Beware of nonrelated PSMA-positive pathology </li>
            <li>
              <span>
                Increased uptake may be seen in axillary lymph nodes following COVID-19 vaccination and may persist for
                8 weeks
                <sup className={classes.superscript}>2</sup>
              </span>
            </li>
          </ul>
          <div className={classes.gangliaImages}>
            <img
              className={classes.image1}
              src={`${BASE_URL}${IMAGE_PATHS.ANTERIOR_VIEW_OF_GANGLIA.path}`}
              alt={`${IMAGE_PATHS.ANTERIOR_VIEW_OF_GANGLIA.alt}`}
            />
            <img
              className={classes.image2}
              src={`${BASE_URL}${IMAGE_PATHS.LATERAL_VIEW_OF_GANGLIA.path}`}
              alt={`${IMAGE_PATHS.LATERAL_VIEW_OF_GANGLIA.alt}`}
            />
          </div>
          <Typography className={classes.imageText}>
            <strong>Anterior (A) and lateral (B) view of the cervical ganglia</strong>
            <sup className={classes.superscript}>1</sup>
          </Typography>
          <Typography variant="body1" className={classes.reviewFindingsText}>
            <strong>See arrows for the location of the lesion and review findings</strong>
          </Typography>
          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.VIRCHOW_PET_ARROWS.path}`}
            alt={`${IMAGE_PATHS.VIRCHOW_PET_ARROWS.alt}`}
          />
          <Typography variant="body2">
            <strong>Findings:</strong>
          </Typography>
          <ul className={classes.poslumaList}>
            <li>Virchow node with clearly increased uptake consistent with a positive lesion</li>
          </ul>
          <FindingsPanel
            trainingId={2}
            module="poslumaInterpretation"
            page={13}
            // setNextDisabledFindings={setNextDisabledFindings}
          />
          <Typography variant="h4">Skeleton</Typography>
          <Typography variant="body1">
            <strong>Assessment Procedures</strong>
            <sup className={classes.superscript}>1</sup>:
          </Typography>
          <ul className={classes.poslumaList}>
            <li>The suggested SUV scale is 0-10</li>
            <li>Review the skeleton for focally increased uptake</li>
            <li>Review and match any finding with the corresponding anatomical imaging</li>
          </ul>
          <Typography variant="body1">
            <strong>Possible variant uptake or interpretation challenge</strong>
            <sup className={classes.superscript}>1</sup>:
          </Typography>
          <ul className={classes.poslumaList}>
            <li>
              <span>
                In instances of a single or low number of focally increased areas of uptake,&nbsp;
                <strong>
                  <u>especially the ribs</u>
                </strong>
                , the presence or absence of sclerosis on anatomical imaging must be considered
              </span>
            </li>
            <li>
              <span>
                Many benign osseous findings (e.g., degenerative changes, fractures, benign bone lesions) may take up
                POSLUMA and should not be mistakenly interpreted as bone metastases of prostate cancer
              </span>
            </li>
          </ul>
          <CaseStudy
            trainingId={2}
            module="poslumaInterpretation"
            page={13}
            index={0}
            setNextDisabledCS={setNextDisabledCS}
          />
          <CaseStudy
            trainingId={2}
            module="poslumaInterpretation"
            page={13}
            index={1}
            setNextDisabledCS={setNextDisabledCS}
          />
        </div>
      </div>
      <LearningCheckQuestions
        module="poslumaInterpretation"
        page={13}
        setNextDisabled={setNextDisabled}
        trainingId={2}
      />
    </>
  );
};

export default InterpretationPage13;
